import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './ProjectDashboard.module.css';
import jobDashboardStyles from '../JobDashboard/HomeUserJobsDashboard.module.css'; // Import styles from JobDashboard
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader2 from '../../components/loader2';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import api from '../../api/api.js';
import showToast from '../../components/ShowToast';
import { Card, CardContent, CardHeader, CardTitle } from "../../_newprofiles/components/ui/Card";
import { Plus } from "lucide-react";
import AddEditProjectModal from './components/AddEditProjectModal';
import CreateProjectModal from '../../components/Projects/CreateProjectModal'; // Import the new modal

const ProjectDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false); // Renamed for clarity
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false); // New state for the new modal
  const [selectedProject, setSelectedProject] = useState(null); // For editing
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState('');
  const [homeUserId, setHomeUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    let uid = null;
    let uname = '';
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        uname = decodedToken.UserName || localStorage.getItem('loggedInUsername') || 'User';
        uid = decodedToken.nameid || localStorage.getItem('loggedInHomeUserId');
      } catch (e) {
        console.warn("JWT decode failed, falling back to storage:", e);
        uname = localStorage.getItem('loggedInUsername') || 'User';
        uid = localStorage.getItem('loggedInHomeUserId');
      }
    } else {
      uname = localStorage.getItem('loggedInUsername') || '';
      uid = localStorage.getItem('loggedInHomeUserId');
    }
    if (uname) setUsername(uname);
    if (uid) setHomeUserId(uid);
    if (!uid) setError("No user identified. Please log in.");
  }, []);

  const fetchProjects = async () => {
    if (!homeUserId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/Projects/getmyprojects?homeUserId=${homeUserId}`);
      setProjects(response.data);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (homeUserId) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeUserId]);

  const openAddEditModal = (project = null) => { // Renamed function
    setSelectedProject(project);
    setIsAddEditModalOpen(true);
  };

  const closeAddEditModal = () => { // Renamed function
    setSelectedProject(null);
    setIsAddEditModalOpen(false);
  };

  const openCreateProjectModal = () => { // New function to open the new modal
    setIsCreateProjectModalOpen(true);
  };

  const closeCreateProjectModal = () => { // New function to close the new modal
    setIsCreateProjectModalOpen(false);
  };

  const handleSaveProject = async (projectData) => {
    // This function will handle both creating and updating projects.
    const token = localStorage.getItem('token');
    if (!token) {
      showToast('error', 'You must be logged in to save a project.');
      return;
    }

    if (!homeUserId) {
      showToast('error', 'Could not identify user. Please log in again.');
      return;
    }

    const projectPayload = {
      ...projectData,
      homeUserId: homeUserId,
      status: selectedProject ? selectedProject.status : "Planning", // Keep existing status or default to Planning
      jobs: [],
    };

    if (selectedProject && selectedProject.projectId) {
      // Update existing project
      projectPayload.projectId = selectedProject.projectId;
      try {
        await api.put(`/api/Projects/${selectedProject.projectId}`, projectPayload);
        showToast('success', 'Project updated successfully!');
      } catch (error) {
        showToast('error', 'Failed to update project.');
        console.error("Error updating project:", error);
      }
    } else {
      // Create new project
      try {
        await api.post('/api/Projects', projectPayload);
        showToast('success', 'Project created successfully!');
      } catch (error) {
        showToast('error', 'Failed to create project.');
        console.error("Error creating project:", error);
      }
    }
    
    closeAddEditModal(); // Use the renamed close function
    fetchProjects();
  };

  const stats = {
    totalProjects: projects.length,
    activeProjects: projects.filter((project) => project.status === "Planning" || project.status === "InProgress").length,
    totalJobs: projects.reduce((sum, project) => sum + project.jobCount, 0),
    activeJobs: 0, // This would require fetching all jobs and filtering by status.
    totalQuotes: 0, // This would require fetching all jobs.
    totalBudget: 0, // This would require fetching all jobs.
    completedJobs: 0, // This would require fetching all jobs.
  };

  return (
    <>
      <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'projects'} />
      <div className={styles.pageContainer}>
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h1>My Projects</h1>
            <button className={styles.createProjectButton} onClick={openCreateProjectModal}> {/* Changed onClick */}
              <Plus size={20} className={styles.buttonIcon} />
              Create New Project
            </button>
          </div>
        </header>

        {/* Stats Cards */}
        <div className={`${styles.statsContainer}`}>
          <div className={`${styles.card} ${styles.bgGradientBlueCard} ${styles.statCard}`}>
            <div className={styles.statCardContent}>
              <div className={styles.statCardText}>
                <p className={styles.statCardLabel}>Total Projects</p>
                <p className={styles.statCardValue}>{stats.totalProjects}</p>
                <p className={styles.statCardSubtitle}>{stats.activeProjects} active</p>
              </div>
              <span className={styles.statCardIcon}>📁</span>
            </div>
          </div>
          <div className={`${styles.card} ${styles.bgGradientBlueCard} ${styles.statCard}`}>
            <div className={styles.statCardContent}>
              <div className={styles.statCardText}>
                <p className={styles.statCardLabel}>Total Jobs</p>
                <p className={styles.statCardValue}>{stats.totalJobs}</p>
                <p className={styles.statCardSubtitle}>{stats.activeJobs} active</p>
              </div>
              <span className={styles.statCardIcon}>📄</span>
            </div>
          </div>
          <div className={`${styles.card} ${styles.bgGradientAmberCard} ${styles.statCard}`}>
            <div className={styles.statCardContent}>
              <div className={styles.statCardText}>
                <p className={styles.statCardLabel}>Total Quotes</p>
                <p className={styles.statCardValue}>{stats.totalQuotes}</p>
                <p className={styles.statCardSubtitle}>From contractors</p>
              </div>
              <span className={`${styles.statCardIcon} ${styles.textAmber200}`}>💬</span>
            </div>
          </div>
          <div className={`${styles.card} ${styles.bgGradientGreenCard} ${styles.statCard}`}>
            <div className={styles.statCardContent}>
              <div className={styles.statCardText}>
                <p className={styles.statCardLabel}>Total Spend</p>
                <p className={styles.statCardValue}>${(stats.totalBudget / 1000).toFixed(0)}K</p>
                <p className={styles.statCardSubtitle}>Across all jobs</p>
              </div>
              <span className={`${styles.statCardIcon} ${styles.textGreen200}`}>💰</span>
            </div>
          </div>
        </div>

        <main className={styles.mainContent}>
          {isLoading && <Loader2 />}
          {error && <p className={styles.errorState}>Error: {error}</p>}
          {!isLoading && !error && projects.length === 0 && (
            <div className={styles.emptyState}>
              <p>No projects yet. Click "Create New Project" to get started.</p>
            </div>
          )}
          {!isLoading && !error && projects.length > 0 && (
            <div className={styles.projectsGrid}>
              {projects.map((project) => (
                <Link to={`/project/${project.projectId}`} key={project.projectId} className={styles.projectCardLink}>
                  <Card className={styles.projectCard}>
                    <CardHeader>
                      <CardTitle className={styles.projectCardTitle}>{project.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={styles.projectDescription}>{project.description}</p>
                      <div className={styles.projectMeta}>
                        <span className={styles.projectStatus}>Status: {project.status}</span>
                        <span className={styles.projectJobCount}>{project.jobCount} Jobs</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>

      <AddEditProjectModal
        isOpen={isAddEditModalOpen}
        onClose={closeAddEditModal}
        onSave={handleSaveProject}
        project={selectedProject}
      />

      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={closeCreateProjectModal}
      />
    </>
  );
};

export default ProjectDashboard;
